// Route: /unsafe-sign-on
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  AlertTitle, 
  Button, 
  Container 
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';

function UnsafeSignOn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Prevents double execution during React strict mode
  const signOnAttempted = useRef(false);
  const { setUser } = useAuth();
  
  useEffect(() => {
    async function signOn() {
      // Ensure signOn is only executed once
      if (signOnAttempted.current) return;
      signOnAttempted.current = true;

      // Parse query parameters
      const searchParams = Object.fromEntries(new URLSearchParams(location.search));
      
      try {
        const response = await fetch('/api/unsafe-auth/custom-sign-on', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(searchParams),
        });

        if (response.redirected) {
          window.location.href = response.url;
          return;
        }

        if (!response.ok) {
          const data = await response.json();
          if (response.status >= 300 && response.status < 400) {
            // Handle any 3xx status that wasn't caught by response.redirected
            navigate(data.redirectUrl || '/');
          } else if (response.status >= 400 && response.status < 500) {
            setError(`Authentication failed: ${data.message || data.error || 'Invalid credentials'}`);
          } else if (response.status >= 500) {
            setError(`Server error: ${data.message || data.error || 'Something went wrong'}`);
          }
        } else {
          // Parse the successful response
          const data = await response.json();
          
          // Save token to localStorage if it exists in the response
          if (data.token) {
            localStorage.setItem('token', data.token);
            setUser(data.user);
          }
          
          console.log('Now redirecting to home page');
          // If redirect wasn't handled by response.redirected
          navigate('/projects');
        }
      } catch (error) {
        console.error('Failed to connect to authentication service:', error);
        setError('Failed to connect to authentication service');
      } finally {
        setIsLoading(false);
      }
    }

    signOn();
  }, [location.search, navigate]);

  return (
    <Container maxWidth="sm">
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Box sx={{ textAlign: 'center', width: '100%', p: 3 }}>
          {isLoading ? (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Processing Sign On
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we verify your credentials...
              </Typography>
            </>
          ) : error ? (
            <>
              <Typography variant="h4" component="h1" gutterBottom>
                Authentication Error
              </Typography>
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
            </>
          ) : null}
        </Box>
      </Box>
    </Container>
  );
}

export default UnsafeSignOn;
